<template>
  <div class="uploader">
    <div class="uploader__zone" tabindex="0" @paste="onPaste">
      <div class="uploader__left">
        <div class="uploader__icon"><i class="el-icon-upload2" /></div>
        <div>
          <div class="uploader__title">{{ $t('common.pasteHint') }}</div>
          <div class="muted uploader__sub">{{ $t('common.pasteHintSub') }}</div>
          <div class="muted uploader__sub">{{ $t('common.uploadLimit', { max: maxLabel }) }}</div>
        </div>
      </div>
      <div class="uploader__btnWrap">
        <el-button size="small" :loading="uploading" :disabled="uploading" @click="choose">{{ $t('common.upload') }}</el-button>
        <input ref="input" type="file" multiple class="uploader__inputHidden" :disabled="uploading" @change="onChoose" />
      </div>
    </div>

    <div v-if="internalList.length > 0" class="uploader__list">
      <div v-for="f in internalList" :key="f.id || f._tmpKey" class="uploader__item">
        <span class="uploader__name" :title="f.originalName || ('#' + f.id)">{{ f.originalName || ('#' + f.id) }}</span>
        <span class="muted" style="font-size:12px;">{{ formatSize(f.size) }}</span>
        <el-button type="text" size="mini" @click="remove(f)"><i class="el-icon-close" /></el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { withApiBase } from '../api/base';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: 'FileUploader',
  props: {
    value: { type: Array, default: null } // v-model: uploaded file objects
  },
  data() {
    return {
      uploading: false,
      internalList: [],
      // File ids that have been "committed" to a record (ticket/dispute/task/receipt) and should never be auto-deleted
      // by draft cleanup hooks (close dialog / reset form / destroy component).
      committedIds: {},
      // Keep frontend limit aligned with backend multipart limits.
      maxBytes: 200 * 1024 * 1024,
      // Multipart upload tuning (S3/MinIO direct upload).
      mpConcurrency: 5,
      // Used to cancel/ignore late upload results (e.g. user closes dialog / resets form mid-upload).
      // NOTE: Do not start data keys with "_" in Vue 2; they are not proxied onto `this`.
      uploadSeq: 0,
      idlePromise: Promise.resolve(),
      idleResolve: null
    };
  },
  computed: {
    maxLabel() {
      return '200MB';
    }
  },
  created() {
    this.syncFromValue();
  },
  beforeDestroy() {
    this.cleanupFileList(this.internalList);
    this.cancelUploads();
  },
  watch: {
    value: {
      deep: true,
      handler() {
        const prev = Array.isArray(this.internalList) ? this.internalList.slice() : [];
        this.syncFromValue();
        // Parent cleared v-model: treat previous uploaded files as abandoned draft attachments and try to delete them.
        if (Array.isArray(this.value) && this.value.length === 0 && prev.length > 0) {
          this.cleanupFileList(prev);
        }
        // If the parent cleared v-model while we were uploading, cancel logical in-flight results to avoid
        // "late upload result" being written back into a new form (wrong ticket/task/dispute).
        if (this.uploading && Array.isArray(this.value) && this.value.length === 0) {
          this.cancelUploads();
        }
      }
    }
  },
  methods: {
    hashString(s) {
      // Small stable hash for localStorage keys (avoid huge keys for long filenames).
      let h = 5381;
      const str = String(s || '');
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h + str.charCodeAt(i)) | 0;
      }
      return (h >>> 0).toString(16);
    },
    getCookie(name) {
      try {
        const safe = String(name || '').replace(/[-[\]{}()*+?.,^$|#\s]/g, '\\$&');
        const m = document.cookie.match(new RegExp('(?:^|; )' + safe + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : '';
      } catch (e) {
        return '';
      }
    },
    async apiJson(method, url, body, retried) {
      const csrf = this.getCookie('XSRF-TOKEN');
      const headers = {};
      if (csrf) headers['X-XSRF-TOKEN'] = csrf;
      if (body !== undefined) headers['Content-Type'] = 'application/json';

      const doOnce = async () => {
        const resp = await fetch(withApiBase(url), {
          method,
          credentials: 'include',
          headers,
          body: body === undefined ? undefined : JSON.stringify(body)
        });
        const status = resp.status || 0;
        let data = null;
        const text = await resp.text();
        if (text && (text.trim().startsWith('{') || text.trim().startsWith('['))) {
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = text;
          }
        } else {
          data = text || null;
        }

        if (resp.ok) return data;

        const msg = (data && data.message) || (typeof data === 'string' ? data : null) || `Request failed (${status})`;
        const err = new Error(msg);
        err.status = status;
        err.data = data;
        throw err;
      };

      try {
        return await doOnce();
      } catch (e) {
        if (!retried && e && e.status === 403) {
          try {
            await fetch(withApiBase('/api/auth/csrf'), { credentials: 'include' });
          } catch (ignored) {}
          return await this.apiJson(method, url, body, true);
        }
        throw e;
      }
    },
    markCommitted(ids) {
      // Call this after a successful submit API call to avoid draft-cleanup DELETEs (which would 409 once referenced).
      let list = null;
      if (Array.isArray(ids)) {
        if (ids.length > 0 && typeof ids[0] === 'object') {
          list = ids.map((x) => (x ? x.id : null));
        } else {
          list = ids;
        }
      } else {
        list = (this.internalList || []).map((x) => (x ? x.id : null));
      }
      for (const id of list) {
        if (id != null) this.committedIds[id] = true;
      }
    },
    isCommitted(id) {
      return !!(id != null && this.committedIds && this.committedIds[id]);
    },
    async deleteUploadedFile(id, retried) {
      if (id == null) return;
      const csrf = this.getCookie('XSRF-TOKEN');
      try {
        const resp = await fetch(withApiBase(`/api/files/${id}`), {
          method: 'DELETE',
          credentials: 'include',
          headers: csrf ? { 'X-XSRF-TOKEN': csrf } : {}
        });
        if (resp.ok) return;
        // 404: already deleted / never existed; 409: already referenced by a ticket/task/receipt (not an orphan).
        if (resp.status === 404 || resp.status === 409) return;
        if (!retried && resp.status === 403) {
          try {
            await fetch(withApiBase('/api/auth/csrf'), { credentials: 'include' });
          } catch (ignored) {}
          return await this.deleteUploadedFile(id, true);
        }
      } catch (e) {}
    },
    deleteUploadedFileSilent(id) {
      // Fire-and-forget; never block UI teardown or show user-facing errors.
      this.deleteUploadedFile(id, false);
    },
    getMpCacheKey(file) {
      const fp = `${file && file.name ? file.name : ''}|${file && file.size != null ? file.size : 0}|${file && file.lastModified != null ? file.lastModified : 0}`;
      return { fp, key: `ts_mp_${this.hashString(fp)}` };
    },
    getCachedMpSession(file) {
      const { fp, key } = this.getMpCacheKey(file);
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return { key, fp, sessionId: null };
        const json = JSON.parse(raw);
        if (json && json.fp === fp && json.sessionId) return { key, fp, sessionId: String(json.sessionId) };
      } catch (e) {}
      return { key, fp, sessionId: null };
    },
    cacheMpSession(file, sessionId) {
      const { fp, key } = this.getMpCacheKey(file);
      try {
        localStorage.setItem(key, JSON.stringify({ fp, sessionId }));
      } catch (e) {}
      return key;
    },
    clearCachedMpSession(cacheKey) {
      try {
        if (cacheKey) localStorage.removeItem(cacheKey);
      } catch (e) {}
    },
    async putPresigned(url, blob) {
      const resp = await fetch(url, {
        method: 'PUT',
        // Do NOT include credentials; presigned URL authorizes the request.
        body: blob
      });
      if (resp.ok) return true;
      const err = new Error(`Part upload failed (${resp.status})`);
      err.status = resp.status || 0;
      throw err;
    },
    async uploadViaMultipart(file, seq) {
      // Try to resume an existing multipart session for the same file fingerprint.
      const cached = this.getCachedMpSession(file);
      let sessionId = cached.sessionId;
      let cacheKey = cached.key;
      let partSize = 0;
      let partCount = 0;
      let uploadedParts = [];

      if (sessionId) {
        try {
          const st = await this.apiJson('GET', `/api/files/multipart/${sessionId}`, undefined, false);
          if (st && st.size === file.size && st.partSize && st.partCount) {
            partSize = Number(st.partSize) || 0;
            partCount = Number(st.partCount) || 0;
            uploadedParts = Array.isArray(st.uploadedParts) ? st.uploadedParts : [];
          } else {
            sessionId = null;
            this.clearCachedMpSession(cacheKey);
          }
        } catch (e) {
          sessionId = null;
          this.clearCachedMpSession(cacheKey);
        }
      }

      if (!sessionId) {
        const init = await this.apiJson(
          'POST',
          '/api/files/multipart/init',
          { originalName: file.name, contentType: file.type || null, size: file.size },
          false
        );
        sessionId = init && init.sessionId ? String(init.sessionId) : null;
        partSize = init && init.partSize ? Number(init.partSize) : 0;
        partCount = init && init.partCount ? Number(init.partCount) : 0;
        if (!sessionId || !partSize || !partCount) throw new Error('Multipart init failed');
        cacheKey = this.cacheMpSession(file, sessionId);
        uploadedParts = [];
      }

      const uploadedSet = new Set((uploadedParts || []).map((x) => Number(x)).filter((x) => x > 0));
      const need = [];
      for (let i = 1; i <= partCount; i++) {
        if (!uploadedSet.has(i)) need.push(i);
      }

      if (need.length > 0) {
        const signed = await this.apiJson('POST', `/api/files/multipart/${sessionId}/sign`, { partNumbers: need }, false);
        const urls = signed && signed.urls ? signed.urls : {};
        const partToUrl = (pn) => urls[pn] || urls[String(pn)] || null;

        let idx = 0;
        const uploadOne = async () => {
          while (idx < need.length) {
            const pn = need[idx++];
            if (seq !== this.uploadSeq) return; // canceled/reset

            const start = (pn - 1) * partSize;
            const end = Math.min(start + partSize, file.size);
            const blob = file.slice(start, end);

            let url = partToUrl(pn);
            let attempt = 0;
            while (attempt < 3) {
              if (seq !== this.uploadSeq) return;
              try {
                if (!url) {
                  const s2 = await this.apiJson(
                    'POST',
                    `/api/files/multipart/${sessionId}/sign`,
                    { partNumbers: [pn] },
                    false
                  );
                  const u2 = s2 && s2.urls ? s2.urls : {};
                  url = u2[pn] || u2[String(pn)] || null;
                }
                await this.putPresigned(url, blob);
                break;
              } catch (e) {
                attempt += 1;
                url = null; // force re-sign next loop
                if (attempt >= 3) throw e;
                await sleep(300 * attempt);
              }
            }
          }
        };

        const workers = [];
        const conc = Math.max(1, Math.min(6, Number(this.mpConcurrency) || 3));
        for (let i = 0; i < conc; i++) workers.push(uploadOne());
        await Promise.all(workers);
      }

      if (seq !== this.uploadSeq) {
        // canceled/reset: keep session in localStorage for possible resume.
        return null;
      }

      const data = await this.apiJson('POST', `/api/files/multipart/${sessionId}/complete`, undefined, false);
      this.clearCachedMpSession(cacheKey);
      return data;
    },
    cleanupFileList(list) {
      if (!Array.isArray(list) || list.length === 0) return;
      for (const f of list) {
        const id = f && f.id != null ? f.id : null;
        if (id != null && !this.isCommitted(id)) this.deleteUploadedFileSilent(id);
      }
    },
    async uploadViaXhr(form, retried) {
      const url = withApiBase('/api/files/upload');
      const csrf = this.getCookie('XSRF-TOKEN');
      const doOnce = () =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url, true);
          xhr.withCredentials = true;
          if (csrf) xhr.setRequestHeader('X-XSRF-TOKEN', csrf);
          xhr.onload = () => {
            const status = xhr.status || 0;
            const text = xhr.responseText || '';
            let json = null;
            if (text && (text.trim().startsWith('{') || text.trim().startsWith('['))) {
              try {
                json = JSON.parse(text);
              } catch (e) {}
            }
            if (status >= 200 && status < 300) return resolve(json);
            const msg = (json && json.message) || text || `Upload failed (${status})`;
            const err = new Error(msg);
            err.status = status;
            err.data = json;
            reject(err);
          };
          xhr.onerror = () => {
            const err = new Error('Upload network error');
            err.status = 0;
            reject(err);
          };
          xhr.onabort = () => {
            const err = new Error('Upload aborted');
            err.status = 0;
            reject(err);
          };
          xhr.send(form);
        });

      try {
        return await doOnce();
      } catch (e) {
        // CSRF self-heal for first-write after browser restart: seed token then retry once.
        if (!retried && e && e.status === 403) {
          try {
            await fetch(withApiBase('/api/auth/csrf'), { credentials: 'include' });
          } catch (ignored) {}
          return await this.uploadViaXhr(form, true);
        }
        throw e;
      }
    },
    syncFromValue() {
      if (Array.isArray(this.value)) {
        this.internalList = this.value.slice();
      }
    },
    setUploading(v) {
      if (!!v === this.uploading) return;
      this.uploading = !!v;
      this.$emit('uploading-change', this.uploading);
      if (this.uploading) {
        this.idlePromise = new Promise((resolve) => {
          this.idleResolve = resolve;
        });
      } else if (this.idleResolve) {
        try {
          this.idleResolve();
        } catch (e) {}
        this.idleResolve = null;
      }
    },
    isUploading() {
      return !!this.uploading;
    },
    waitForIdle() {
      return this.uploading ? this.idlePromise : Promise.resolve();
    },
    cancelUploads() {
      // Increase seq first so async completion handlers become no-ops.
      this.uploadSeq += 1;
      // We intentionally don't abort the underlying HTTP request here (cross-browser/proxy quirks can
      // cause uploads to hang when aborted). Instead, we ignore late results via uploadSeq.
      this.setUploading(false);
    },
    emitList() {
      if (Array.isArray(this.value)) this.$emit('input', this.internalList.slice());
      this.$emit('change', this.internalList.slice());
    },
    choose() {
      if (this.uploading) return;
      if (this.$refs.input) this.$refs.input.click();
    },
    onChoose(e) {
      const files = e && e.target && e.target.files ? Array.from(e.target.files) : [];
      if (this.$refs.input) this.$refs.input.value = '';
      this.uploadFiles(files);
    },
    onPaste(e) {
      const dt = e && e.clipboardData ? e.clipboardData : null;
      if (!dt || !dt.items) return;
      const files = [];
      for (let i = 0; i < dt.items.length; i++) {
        const it = dt.items[i];
        if (it && it.kind === 'file') {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        this.uploadFiles(files);
      }
    },
    async uploadFiles(files) {
      if (!files || files.length === 0) return;
      const total = files.reduce((s, f) => s + (f && f.size ? f.size : 0), 0);
      if (total > this.maxBytes) {
        this.$message.error(this.$t('common.uploadTooLarge', { max: this.maxLabel }));
        return;
      }
      for (const f of files) {
        if (f && f.size != null && f.size > this.maxBytes) {
          this.$message.error(this.$t('common.uploadTooLarge', { max: this.maxLabel }));
          return;
        }
      }
      const seq = (this.uploadSeq += 1);
      this.setUploading(true);
      try {
        for (const f of files) {
          if (seq !== this.uploadSeq) {
            return; // canceled/reset
          }
          let data = null;
          try {
            data = await this.uploadViaMultipart(f, seq);
          } catch (e) {
            const code = e && e.data && e.data.code ? String(e.data.code) : '';
            const fallback = e && (e.status === 404 || (e.status === 400 && code === 'MP_NOT_SUPPORTED'));
            if (!fallback) throw e;
            const form = new FormData();
            form.append('file', f);
            // Use XHR for uploads to avoid edge cases where axios/form-data requests are not dispatched in some browsers.
            data = await this.uploadViaXhr(form, false);
          }

          if (seq !== this.uploadSeq) {
            // Upload finished after user canceled/reset: delete the orphan file on backend.
            const id = data && data.id != null ? data.id : null;
            if (id != null && !this.isCommitted(id)) this.deleteUploadedFileSilent(id);
            return;
          }
          if (!data) return;
          this.internalList.push(data);
          this.$emit('uploaded', data);
          this.emitList();
        }
      } catch (e) {
        // If user canceled/reset, ignore late errors (don't toast during navigation/teardown).
        if (seq !== this.uploadSeq) return;
        const status = e && e.status ? Number(e.status) : 0;
        if (status === 401) {
          try {
            if (this.$store) this.$store.dispatch('logout', { remote: false });
          } catch (ignored) {}
          try {
            if (this.$router) this.$router.replace('/login');
          } catch (ignored) {}
        }
        if (status === 423) {
          const announcementId = e && e.data && e.data.details ? e.data.details.announcementId : null;
          try {
            if (this.$route && this.$route.fullPath) this.$store.commit('setAnnouncementReturnTo', this.$route.fullPath);
          } catch (ignored) {}
          try {
            if (this.$store) {
              this.$store.commit('setAnnouncementGate', {
                mustConfirmAnnouncement: true,
                unconfirmedAnnouncementId: announcementId
              });
            }
          } catch (ignored) {}
          try {
            if (this.$router) this.$router.replace('/announcements/confirm');
          } catch (ignored) {}
        }
        const msg = (e && e.message) || this.$t('common.uploadFailed');
        this.$message.error(msg);
      } finally {
        // Only end "uploading" if we are still in the same seq (avoid flipping state after cancel/reset).
        if (seq === this.uploadSeq) this.setUploading(false);
      }
    },
    remove(f) {
      const id = f && f.id ? f.id : null;
      if (id != null) {
        if (!this.isCommitted(id)) this.deleteUploadedFileSilent(id);
        this.internalList = this.internalList.filter((x) => x && x.id !== id);
      } else {
        this.internalList = this.internalList.filter((x) => x !== f);
      }
      this.emitList();
    },
    formatSize(n) {
      if (!n && n !== 0) return '';
      const kb = n / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      const mb = kb / 1024;
      if (mb < 1024) return `${mb.toFixed(1)} MB`;
      const gb = mb / 1024;
      return `${gb.toFixed(1)} GB`;
    }
  }
};
</script>

<style scoped>
.uploader {
  max-width: 100%;
  min-width: 0;
}
.uploader__zone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px dashed rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.45);
  outline: none;
}
.uploader__btnWrap {
  position: relative;
  display: inline-block;
}
.uploader__inputHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.uploader__zone:focus {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
}
.uploader__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.uploader__icon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.6);
}
.uploader__title {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uploader__sub {
  font-size: 12px;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uploader__list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
}
.uploader__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.55);
  min-width: 0;
}
.uploader__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
